import { PageType } from '@libs/common-next';
import type { CartUI } from '@libs/common-node';
import type { Nullable } from '@libs/core';

import { CART_PAGE, CHECKOUT_PAGE } from './constants';
import { getCartLineItemsSignature } from './getCartLineItemsSignature';

export type PostSignInCartMergeNavDecision =
    | { kind: 'clear_proceed_and_stay' }
    | { kind: 'push'; path: string }
    | { kind: 'noop' };

export type ResolvePostSignInCartMergeNavArgs = {
    pageType: PageType;
    isCheckoutLogin: boolean | undefined;
    updatedCart?: Nullable<CartUI>;
    guestCartLineItemsSignature: string | null | undefined;
    shouldRedirectToCheckout: (cart: CartUI) => boolean;
};

export function resolvePostSignInCartMergeNavigation({
    pageType,
    isCheckoutLogin,
    updatedCart,
    guestCartLineItemsSignature,
    shouldRedirectToCheckout,
}: ResolvePostSignInCartMergeNavArgs): PostSignInCartMergeNavDecision {
    const isCart = pageType === PageType.CART;
    const onCheckoutLoginPage = Boolean(isCheckoutLogin);
    const hasNotifications = Boolean(updatedCart?.notifications?.length);
    const merged = Boolean(updatedCart?.merged);

    if (isCart && (hasNotifications || merged)) {
        return { kind: 'clear_proceed_and_stay' };
    }

    if (hasNotifications && onCheckoutLoginPage) {
        return { kind: 'push', path: CART_PAGE };
    }

    if (isCart && updatedCart?.id) {
        if (shouldRedirectToCheckout(updatedCart)) {
            return { kind: 'push', path: CHECKOUT_PAGE };
        }
        return { kind: 'noop' };
    }

    if (onCheckoutLoginPage) {
        // `merged` is also set when the customer's persisted cart was empty, so only a line-item diff
        // against the guest cart means the customer must review the cart.
        const mergedSignature = getCartLineItemsSignature(updatedCart ?? null);
        const mergedCartDiffersFromGuest = mergedSignature !== guestCartLineItemsSignature;
        return { kind: 'push', path: mergedCartDiffersFromGuest ? CART_PAGE : CHECKOUT_PAGE };
    }

    return { kind: 'push', path: CHECKOUT_PAGE };
}
